import { useEffect, useState } from "react";
import { useSnackbar } from 'notistack';
// @mui
import {
  IconButton,
  Popover,
  Button,
  Card,
  Stack,
  Typography,
  MenuItem,
} from "@mui/material";
// sections
import {
  NoTrackingRecord,
  TrackingRecordTable,
  TrackingRecordList,
  AddPastTimeDialog,
  CoreTimeIntroDialog,
  AttendancePoilcyDialog,
  CoreTimeChangeDialog,
} from "./";
import {
  CoreTimeGuideDialog,
  CoreTimeDialog,
} from '../../sections/profile';
// components
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
// hooks
import useResponsive from '../../hooks/useResponsive';
import useLocalStorage from '../../hooks/useLocalStorage';
// utils
import axios from '../../utils/axios';

// ----------------------------------------------------------------------

export default function TimeTrackingRecords({
  companyId,
  totalCount,
  trackingData,
  reloadData,
  contractId=null,
  coreTimeUpdated=null,
  initialCoreTime=null,
}) {
  const smUp = useResponsive('up', 'sm');
  const { enqueueSnackbar } = useSnackbar();

  // core time
  const [coreIntroStorage, setCoreIntroStorage] = useLocalStorage('dioCrewCoreTimeIntroDialog', true);
  const [coreTimeIntroOpen, setCoreTimeIntroOpen] = useState(false);
  const [openCoreTimeGuide, setOpenCoreTimeGuide] = useState(false);
  const [isOpenCoretimeDialog, setIsOpenCoretimeDialog] = useState(false);
  const [submittingCoreTime, setSubmittingCoreTime] = useState(false);
  const [isOpenCoreTimeChangeDialog, setIsOpenCoreTimeChangeDialog] = useState(false);
  const [pendingCoreTime, setPendingCoreTime] = useState(null);
  // attendance
  const [isOpenAttendancePolicyDialog, setIsOpenAttendancePolicyDialog] = useState(false);
  // data
  const [lastId, setLastId] = useLocalStorage(`dioCrewBiz${companyId}TimeId`, 0);
  const [lastReadId, setLastReadId] = useState(0);
  const [isPastTimeDialogOpen, setIsPastTimeDialogOpen] = useState(false);
  // moremenu
  const [moreMenuOpen, setMoreMenuOpen] = useState(null);

  useEffect(() => {
    if ((trackingData?.length > 0) && lastId < trackingData[0]?.id) {
      setLastId(trackingData[0]?.id);
    }
    setLastReadId(lastId);
  }, []);

  const goEditCoreTime = () => {
    setIsOpenCoretimeDialog(true);
    setOpenCoreTimeGuide(false);
  };

  const closeCoreTimeIntro = () => {
    setCoreIntroStorage(false);
    setCoreTimeIntroOpen(false);
  };

  const goToSetCoreTime = () => {
    closeCoreTimeIntro();
    setIsOpenCoretimeDialog(true);
  };

  const setCoreTime = (coreTime) => {
    if (coreTimeUpdated) {
      // 기존 코어타임 변경하는 경우 - change dialog 띄우기
      setPendingCoreTime(coreTime);
      setIsOpenCoretimeDialog(false);
      setIsOpenCoreTimeChangeDialog(true);
    } else {
      // 최초 등록하는 경우 - 바로 API 호출
      setIsOpenCoretimeDialog(false);
      registerCoreTime(coreTime);
    }
  };

  const registerCoreTime = async (coreTime) => {
    try {
      setSubmittingCoreTime(true);
      await axios.post(`/api/works/contract/crew/company/${companyId}/${contractId}/core-time/`, { 
        core_time: coreTime
      });
      reloadData();
      enqueueSnackbar('코어 타임이 등록되었습니다.');
      setSubmittingCoreTime(false);
    } catch (err) {
      console.error(err);
      setSubmittingCoreTime(false);
      enqueueSnackbar('코어 타임 설정중 에러가 발생하였습니다. 관리자에 문의해주세요', { variant: 'error' });
    }
  };

  const confirmCoreTimeChange = async (coreTime, reason) => {
    try {
      setSubmittingCoreTime(true);
      await axios.post(`/api/works/contract/crew/company/${companyId}/${contractId}/core-time/`, { 
        core_time: coreTime,
        reason: reason
      });
      reloadData();
      enqueueSnackbar('코어 타임이 변경되었습니다.');
      setSubmittingCoreTime(false);
      setIsOpenCoreTimeChangeDialog(false);
      setPendingCoreTime(null);
    } catch (err) {
      console.error(err);
      setSubmittingCoreTime(false);
      enqueueSnackbar('코어 타임 설정중 에러가 발생하였습니다. 관리자에 문의해주세요', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (trackingData) {
      setCoreTimeIntroOpen((contractId !== null) && coreIntroStorage === 'true' || coreIntroStorage === true);
      setOpenCoreTimeGuide((contractId !== null) && coreIntroStorage !== 'true' && coreIntroStorage !== true && !coreTimeUpdated);
    }
  }, [trackingData]);

  const addCommute = async (commuteData) => {
    try {
      await axios.post(`/api/works/time-tracking/crew/company/${companyId}/past/`, commuteData);
      enqueueSnackbar('근무 기록이 수동 등록되었습니다.', { variant: 'success' });
      reloadData();
      setIsPastTimeDialogOpen(false);
    } catch (err) {
      if (err.response && err.response.status === 400) {
        enqueueSnackbar(err.response.data.message, { variant: 'warning' });
      }
      console.error(err);
    }
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">
          근무 기록 {totalCount}건
        </Typography>

        <Stack spacing={1} direction="row" alignItems="center">
          <Button
            disableFocusRipple
            variant="outlined"
            sx={{ ml: -1, justifyContent: "left" }}
            startIcon={<Iconify icon="ic:round-plus" />}
            size="medium"
            onClick={() => setIsPastTimeDialogOpen(true)}
          >
            출퇴근 수동 등록
          </Button>

          <MoreMenu
            open={moreMenuOpen}
            setOpen={setMoreMenuOpen}
            actions={
              <>
              <MenuItem onClick={() => { setIsOpenCoretimeDialog(true); setMoreMenuOpen(false); }} sx={{ height: "46px" }}>
                <Iconify icon="mdi:clock-time-three-outline" sx={{ mr: 1.5, width: 18, height: 18 }} />
                코어 타임 설정
              </MenuItem>
              <MenuItem onClick={() => { setIsOpenAttendancePolicyDialog(true); setMoreMenuOpen(false); }} sx={{ height: "46px" }}>
                <Iconify icon="eva:info-fill" sx={{ mr: 1.5, width: 18, height: 18 }} />
                출퇴근 정책
              </MenuItem>
              </>
            }
          />
        </Stack>
      </Stack>

      {trackingData?.length === 0 && (
        <NoTrackingRecord />
      )}
      {trackingData?.length > 0 && (
        <Card>
          <Typography variant="body2" sx={{ px: 3, pt: 2.5, pb: 0, color: "text.secondary" }}>
            근무 기록은 현지 시간으로 표시됩니다.
          </Typography>
          {smUp && (
            <TrackingRecordTable companyId={companyId} lastId={lastReadId} trackingData={trackingData} reloadData={reloadData} />
          )}
          {!smUp && (
            <TrackingRecordList companyId={companyId} lastId={lastReadId} trackingData={trackingData} reloadData={reloadData} />
          )}
        </Card>
      )}
      <AddPastTimeDialog
        isOpen={isPastTimeDialogOpen}
        closeDialog={() => setIsPastTimeDialogOpen(false)}
        addCommute={addCommute}
      />
      <CoreTimeDialog
        isOpen={isOpenCoretimeDialog}
        closeDialog={() => setIsOpenCoretimeDialog(false)}
        setCoreTime={setCoreTime}
        initialCoreTime={initialCoreTime}
        coreTimeUpdated={coreTimeUpdated}
        submittingCoreTime={submittingCoreTime}
      />
      <CoreTimeGuideDialog
        isOpen={openCoreTimeGuide}
        closeDialog={() => setOpenCoreTimeGuide(false)}
        editCoreTime={goEditCoreTime}
      />
      <CoreTimeIntroDialog
        isOpen={coreTimeIntroOpen}
        close={closeCoreTimeIntro}
        goToSet={goToSetCoreTime}
      />
      <AttendancePoilcyDialog
        isOpen={isOpenAttendancePolicyDialog}
        closeDialog={() => setIsOpenAttendancePolicyDialog(false)}
      />
      <CoreTimeChangeDialog
        isOpen={isOpenCoreTimeChangeDialog}
        closeDialog={() => setIsOpenCoreTimeChangeDialog(false)}
        onConfirm={confirmCoreTimeChange}
        coreTime={pendingCoreTime}
        submitting={submittingCoreTime}
      />
    </Stack>
  )
}

function MoreMenu({ actions, open, setOpen }) {
  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  return (
    <>
      <IconButton onClick={handleOpen}
        sx={{
          ...open&&{ bgcolor: "action.hover" },
          borderRadius: 1,
          border: (theme) => `1px solid ${theme.palette.grey[500_32]}`,
        }}
      >
        <SvgIconStyle src={`/icons/icon-dots-mono.svg`} sx={{ width: 20, height: 20, color: "text.primary" }} />
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            overflow: 'inherit',
            width: 165,
            '& .MuiMenuItem-root': {
              px: 2,
              typography: 'subtitle2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        {actions}
      </Popover>
    </>
  );
};
